/**
 * jr-help.js — the Jasper help chat client.
 *
 * Mounted by snippets/jr-help-chat.liquid. Reads its config from the
 * #JrHelpConfig JSON block (endpoint, preview key, logged-in flag, first
 * name, URLs) and drives the widget with no framework and no third-party
 * request: the only network calls are the `jr-help` edge function and, for
 * signed-in shoppers, POST /apps/jr/ to mint a short-lived customer token.
 *
 * THE STREAM. The chat endpoint answers as text/event-stream. We read it with
 * fetch + ReadableStream rather than EventSource because EventSource can't
 * POST or set headers. Frames are `data: <json>` lines separated by a blank
 * line; a chunk boundary can fall anywhere, so we keep the unparsed tail in a
 * buffer and only decode frames that have their terminating blank line.
 * `reply_delta` events fill the bubble as they arrive; the `answer` event's
 * `reply` is authoritative and replaces the streamed text — they should match,
 * and if the model's stream and the final differ the final wins.
 *
 * THE TOKEN. Signed-in shoppers get an opaque token from the App Proxy (the
 * proxy signs the request, so identity is verified server-side; the theme
 * never sees an email or id). It is cached in sessionStorage and re-minted
 * within 60 s of `exp`. If minting fails the chat continues anonymously and
 * the assistant is left to propose `sign_in` if it needs the account.
 *
 * THE PREVIEW CODE. While the backend is preview-gated, `ping` answers
 * `{preview:true, open:false}` and the widget swaps the composer for a
 * one-line "enter the preview code" step. The code is kept in localStorage
 * (testers reload and navigate constantly) and sent as `x-help-preview`; a
 * 403 on a chat call clears it and asks again. Once the backend is live
 * (`preview:false`) the step never shows and any stored code is ignored.
 *
 * Nothing sensitive is ever logged: warnings name the failure, never the token,
 * the code, or the message text.
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('JrHelpConfig');
  var root = document.getElementById('JrHelp');
  if (!cfgEl || !root) return;

  var CFG;
  try { CFG = JSON.parse(cfgEl.textContent || '{}'); } catch (e) { return; }
  if (!CFG.endpoint) return;

  var MAX_MESSAGE = 2000;
  var HISTORY_TURNS = 8;
  var TRANSCRIPT_CAP = 40;          // messages kept in sessionStorage
  var STREAM_IDLE_MS = 45000;       // no event for this long → treat as failure
  var ERROR_COPY = 'I couldn’t reach our system just now — try again in a moment, or email support@jeremysrazors.com';
  var RATE_COPY = 'Give me a second — you’re sending faster than I can read';
  var ESCALATE_COPY = 'A specialist has this — you’ll hear back by email.';

  var KEY = {
    sid: 'jrhelp:sid',
    transcript: 'jrhelp:transcript',
    token: 'jrhelp:token',
    open: 'jrhelp:open',
    unread: 'jrhelp:unread',
    claimed: 'jrhelp:claimed',  // the session_id already attached to a signed-in customer
    guest: 'jrhelp:guest',      // chose "continue as a guest" (or just started typing)
    csat: 'jrhelp:csat',        // the session_id whose "did that take care of it?" was answered
    preview: 'jrhelp:preview'
  };

  // ---- DOM ---------------------------------------------------------------
  var $ = function (id) { return document.getElementById(id); };
  var launcher = $('JrHelpLauncher');
  var panel = $('JrHelpPanel');
  var closeBtn = $('JrHelpClose');
  var body = $('JrHelpBody');
  var welcome = $('JrHelpWelcome');
  var list = $('JrHelpMessages');
  var chips = $('JrHelpChips');
  var form = $('JrHelpComposer');
  var input = $('JrHelpInput');
  var sendBtn = $('JrHelpSend');
  var access = $('JrHelpAccess');
  var accessInput = $('JrHelpAccessInput');
  var accessBtn = $('JrHelpAccessBtn');
  var accessText = $('JrHelpAccessText');
  var faceVids = [$('JrHelpLauncherVid'), $('JrHelpHeaderVid')];
  if (!launcher || !panel || !list || !form || !input) return;

  // Animate the face the way the console does: muted + playsinline so autoplay
  // is allowed, no loop so it settles on a frame. Cued when the panel opens,
  // when the launcher is hovered, and when Jasper starts a reply — both copies
  // are cued rather than working out which one is visible.
  function playFace() {
    if (reduceMotion()) return;
    for (var i = 0; i < faceVids.length; i++) {
      var v = faceVids[i];
      if (!v || !v.play) continue;
      try { v.currentTime = 0; var p = v.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
    }
  }

  // ---- Storage helpers -----------------------------------------------------
  // The conversation lives in localStorage with a 24-hour TTL, not
  // sessionStorage: signing in on this store leaves the origin (Shopify's
  // customer-accounts login on shopify.com) and may come back in a new tab or
  // window, and a customer who steps away for an hour expects to pick up where
  // they left off. The backend keeps the same conversation under session_id, so
  // the transcript here and the row there stay one thing. Storage can throw in
  // private mode; every access is guarded.
  var TTL_MS = 24 * 60 * 60 * 1000;
  function readJSON(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var box = JSON.parse(raw);
      if (!box || typeof box !== 'object' || !('v' in box)) return null;
      if (box.exp && Date.now() > box.exp) { localStorage.removeItem(key); return null; }
      return box.v;
    } catch (e) { return null; }
  }
  function writeJSON(key, val) {
    try { localStorage.setItem(key, JSON.stringify({ v: val, exp: Date.now() + TTL_MS })); } catch (e) { /* storage full or blocked: chat still works, just doesn't persist */ }
  }
  function remove(key) { try { localStorage.removeItem(key); } catch (e) {} }
  function readLocal(key) { try { return localStorage.getItem(key) || ''; } catch (e) { return ''; } }
  function writeLocal(key, val) { try { if (val) localStorage.setItem(key, val); else localStorage.removeItem(key); } catch (e) {} }

  function uuidv4() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var b = new Uint8Array(16);
    if (window.crypto && crypto.getRandomValues) crypto.getRandomValues(b);
    else for (var i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
    b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
    var h = Array.prototype.map.call(b, function (x) { return ('0' + x.toString(16)).slice(-2); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }

  // ---- State -------------------------------------------------------------
  // ---- A finished conversation stays finished --------------------------------
  // Jon, 2026-09-16: "if we finished the chat it should open up to a fresh
  // one the next time." A conversation is over when the customer answered the
  // "did that take care of it?" question and ten minutes have passed, or when
  // nothing has happened for six hours. Then the next visit starts clean: new
  // session id, empty thread, greeting again. The old conversation is still
  // on the backend and the CRM record; only the browser's copy is retired.
  var LAST_KEY = 'jrhelp:last_at';
  function touch() { writeJSON(LAST_KEY, Date.now()); }
  (function retireIfFinished() {
    var sid = readJSON(KEY.sid);
    if (!sid) return;
    var last = Number(readJSON(LAST_KEY)) || 0;
    var idle = Date.now() - last;
    var answered = readJSON(KEY.csat) === sid;
    var finished = (answered && idle > 10 * 60 * 1000) || idle > 6 * 60 * 60 * 1000;
    if (!finished) return;
    ['sid', 'transcript', 'claimed', 'csat', 'guest', 'open', 'unread'].forEach(function (k) { remove(KEY[k]); });
    remove('jrhelp:evseq:' + sid);
    remove(LAST_KEY);
  })();

  var sessionId = readJSON(KEY.sid);
  if (!sessionId) { sessionId = uuidv4(); writeJSON(KEY.sid, sessionId); }

  // transcript: [{role:'customer'|'assistant', text, actions?:[], escalated?:bool}]
  var transcript = readJSON(KEY.transcript) || [];
  var busy = false;            // a turn is in flight
  var inline = false;          // hosted in the page (/pages/help) rather than floating
  // The cancellation split test (Jon 9/17): 'chat' = Jasper runs it as a
  // conversation; 'flow' = the guided overlay opens in place. Told to us by
  // the backend's meta event (a stable coin flip on the customer id).
  var KEY_CX = 'jrhelp:cx_variant';
  var cxVariant = readJSON(KEY_CX) || null;
  var lastCancel = null;       // the brain's classification from the latest answer (stage/locus/cause/verbatim)
  var pinged = false;          // ping once per page load, on first open
  var userScrolledUp = false;  // don't yank the list if they're reading history
  var lastFocus = null;

  // A signed-out shopper must never send a stale token from an earlier login
  // in the same tab session.
  if (!CFG.loggedIn) remove(KEY.token);

  // ---- Rendering ---------------------------------------------------------
  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function scrollToBottom(force) {
    if (userScrolledUp && !force) return;
    body.scrollTop = body.scrollHeight;
  }
  body.addEventListener('scroll', function () {
    var gap = body.scrollHeight - body.scrollTop - body.clientHeight;
    userScrolledUp = gap > 80;
  });

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  // Bubble text is plain text — except a URL on OUR domain, which becomes a
  // link (the offers doc may point at a verification page, the story at a
  // product). Anything off-domain stays inert text: Jasper never sends a
  // customer to a site we don't run.
  var LINK_RE = /https?:\/\/(?:www\.)?jeremysrazors\.com\/[^\s)]*/g;
  function setBubbleText(node, text) {
    node.textContent = '';
    var t = String(text || ''), last = 0, m;
    LINK_RE.lastIndex = 0;
    while ((m = LINK_RE.exec(t))) {
      if (m.index > last) node.appendChild(document.createTextNode(t.slice(last, m.index)));
      var a = el('a', 'jr-help__link', m[0].replace(/^https?:\/\/(?:www\.)?jeremysrazors\.com/, '').replace(/[.,]$/, '') || '/');
      a.href = m[0].replace(/[.,]$/, '');
      node.appendChild(a);
      last = m.index + m[0].length;
    }
    if (last < t.length) node.appendChild(document.createTextNode(t.slice(last)));
  }

  function avatar() {
    var img = el('img', 'jr-help__msg-avatar');
    img.src = CFG.avatar; img.alt = ''; img.width = 28; img.height = 28;
    return img;
  }

  // Returns the bubble element so streaming can keep writing into it.
  function renderMessage(msg) {
    if (msg.role === 'status') { var s = el('div', 'jr-help__status', msg.text || ''); list.appendChild(s); return s; }
    if (msg.role === 'csat') return null;   // the verdict record; Jasper's reply bubble follows it in the transcript
    var row = el('div', 'jr-help__msg jr-help__msg--' + msg.role);
    if (msg.role === 'assistant' || msg.role === 'error') row.appendChild(avatar());
    var bubble = el('div', 'jr-help__bubble');
    setBubbleText(bubble, msg.text || '');
    row.appendChild(bubble);
    list.appendChild(row);
    if (msg.role === 'assistant') {
      if (msg.cards && msg.cards.length) renderCards(msg.cards);
      if (msg.escalated) list.appendChild(el('div', 'jr-help__status', ESCALATE_COPY));
      if (msg.actions && msg.actions.length) renderActions(msg.actions);
      // An unanswered "did that take care of it?" keeps its two taps on reload.
      if (msg.csat && !csatAnswered()) csatButtons();
    }
    return bubble;
  }

  // Where sign-in comes back to. This store runs Shopify's NEW customer
  // accounts (login on shopify.com); the only route that returns the customer
  // to the storefront afterwards is /customer_authentication/login?return_to=
  // — the same one the account drawer uses. `routes.account_login_url` is
  // /customer_authentication/redirect with its own query string, and
  // /account/login?return_url= lands on the hosted account page (measured
  // 2026-09-16, both signed in and after a fresh login). Shopify appends
  // ?shop_sign_in=true on the way back, which the boot code uses to re-open.
  function signInHref() {
    var here = location.pathname + location.search.replace(/[?&]shop_sign_in=true/, '').replace(/^&/, '?');
    return '/customer_authentication/login?return_to=' + encodeURIComponent(here);
  }

  // Every account action lands on OUR account page (/pages/my-account — the one
  // with everything on it), deep-linked to the section; never Shopify's hosted
  // account page (Jon, 2026-09-16). Order questions are answered by the card
  // (tracking links go to the carrier), so order pills only appear when no card
  // could be resolved, and then they go to our page too.
  function actionHref(a) {
    var acct = CFG.subscriptionsUrl || '/pages/my-account';
    switch (a.kind) {
      case 'sign_in':
        return signInHref();
      case 'open_order':
      case 'resend_tracking':
        return acct;
      case 'skip_next':
      case 'reschedule':
      case 'change_frequency':
      case 'cancel_subscription':
      case 'reactivate':
        return acct + '#subscriptions';
      case 'update_address':
        return acct + '#jr-addr-sec';
      case 'update_payment':
        return acct + '#payment';
      default:
        return null;
    }
  }

  // ---- Cards: figures straight from the record ---------------------------
  // A date-only string ("2026-12-19") is a calendar date, not an instant:
  // `new Date('2026-12-19')` is UTC midnight, which every US timezone renders
  // as the day BEFORE ("next Dec 18" on a Dec 19 charge — seen live 9/20).
  // Parse it as a local date so the card, the confirmation and the account
  // page all name the same day.
  function parseDate(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
  }
  function fmtDate(iso) {
    if (!iso) return '';
    var d = parseDate(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  function fmtMoney(n) { return (typeof n === 'number') ? '$' + n.toFixed(2) : ''; }
  function fmtStatus(s) { return String(s || '').toLowerCase().replace(/_/g, ' '); }

  function renderCards(cards) {
    if (!cards || !cards.length) return;
    var wrap = el('div', 'jr-help__cards');
    cards.forEach(function (c) {
      var card = el('div', 'jr-help__card');
      if (c.kind === 'order') {
        card.appendChild(el('div', 'jr-help__card-t', 'Order #' + String(c.order_number || '').replace(/^#/, '')));
        var meta = [fmtDate(c.placed_at), fmtMoney(c.total_usd), fmtStatus(c.paid), fmtStatus(c.fulfillment)].filter(Boolean).join(' · ');
        if (meta) card.appendChild(el('div', 'jr-help__card-m', meta));
        if (c.items && c.items.length) card.appendChild(el('div', 'jr-help__card-m', c.items.map(function (i) { return (i.qty > 1 ? i.qty + '× ' : '') + i.title; }).join(', ')));
        var t = c.tracking;
        if (t) {
          var row = el('div', 'jr-help__card-track');
          // Carrier status words vary (Amazon says "success" for delivered);
          // read them the way the CRM does.
          var st = String(t.status || '').toLowerCase();
          var state = t.delivered_at ? 'Delivered ' + fmtDate(t.delivered_at)
                    : /success|delivered/.test(st) ? 'Delivered'
                    : t.estimated_delivery_at ? 'Arriving ' + fmtDate(t.estimated_delivery_at)
                    : /transit/.test(st) ? 'In transit'
                    : /fail|cancel|error|exception/.test(st) ? 'Delivery problem'
                    : fmtStatus(t.status) || 'Shipped';
          row.appendChild(el('span', '', [t.carrier, state].filter(Boolean).join(' · ')));
          if (t.url) {
            var link = el('a', 'jr-help__card-link', 'Track package →');
            link.href = t.url; link.target = '_blank'; link.rel = 'noopener';
            link.addEventListener('click', function () { track('card_link_clicked', { kind: 'tracking' }); });
            row.appendChild(link);
          } else if (t.number) {
            row.appendChild(el('span', 'jr-help__card-m', String(t.number)));
          }
          card.appendChild(row);
        }
      } else if (c.kind === 'subscription') {
        card.appendChild(el('div', 'jr-help__card-t', c.product || 'Subscription'));
        // The record's interval may already read "every 3 months" — don't say every twice.
        var everyText = c.every ? (/^every\b/i.test(String(c.every)) ? String(c.every) : 'every ' + c.every) : '';
        var sm = [everyText, fmtMoney(c.price_usd), fmtStatus(c.status), c.next_charge_at ? 'next ' + fmtDate(c.next_charge_at) : ''].filter(Boolean).join(' · ');
        if (sm) card.appendChild(el('div', 'jr-help__card-m', sm));
        var manage = el('a', 'jr-help__card-link', 'Manage →');
        manage.href = (CFG.subscriptionsUrl || '/pages/my-account') + '#subscriptions';
        card.appendChild(manage);
      } else { return; }
      wrap.appendChild(card);
    });
    if (wrap.childNodes.length) list.appendChild(wrap);
  }

  // ---- Actions ---------------------------------------------------------------
  // Subscription changes HAPPEN IN THE CHAT (Jon, 2026-09-16: "Why can't it
  // just happen in the chat?"). The account page's own App Proxy actions —
  // skip / reschedule / set_frequency / reactivate — are called from here with
  // the same signed-in session the page uses; the backend re-checks that the
  // subscription belongs to this customer before touching Recharge, and writes
  // the change through to the mirror so Jasper's next answer sees it. Each one
  // gets a confirm card first (a date or a cadence to pick where needed).
  // Cancelling stays on the account page: it has a deliberate guided flow.
  // Address and payment are forms, so they deep-link to their sections.
  var IN_CHAT = { skip_next: 'skip', reschedule: 'reschedule', change_frequency: 'set_frequency', reactivate: 'reactivate' };

  // After a cadence change Recharge recomputes the next charge from the last
  // order, so the date MOVES (Dec 19 → Jan 19 on 3→4 months, seen live 9/20).
  // Name the new date when the server returns it, and never claim it stayed.
  function nextDateNote(res) {
    var d = res && fmtDate(res.next_charge_at);
    return d ? ' Your next shipment is now ' + d + '.' : ' Your next shipment date has moved to fit the new rhythm — the card above shows it.';
  }

  function callAccount(payload) {
    return fetch('/apps/jr/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok || !j || !j.ok) throw new Error((j && j.error) || ('http ' + r.status));
        return j;
      });
    });
  }

  function btn(cls, text, onClick) {
    var b = el('button', cls, text);
    b.type = 'button';
    b.addEventListener('click', onClick);
    return b;
  }

  function statusLine(text) {
    var note = { role: 'status', text: text };
    pushMessage(note);
    renderMessage(note);
    scrollToBottom(true);
  }

  function closeConfirm() {
    var open = list.querySelector('.jr-help__confirm');
    if (open && open.parentNode) open.parentNode.removeChild(open);
  }

  // Keep whatever just landed at the bottom in view — after layout, so a card
  // that was appended this frame is measured, not the list before it.
  function revealBottom() {
    userScrolledUp = false;
    var go = function () {
      // 'instant' beats any scroll-behavior:smooth the theme sets globally —
      // a smooth scroll re-triggered three times settles short of the bottom.
      try { body.scrollTo({ top: body.scrollHeight, behavior: 'instant' }); } catch (e) { body.scrollTop = body.scrollHeight; }
    };
    go();
    if (window.requestAnimationFrame) requestAnimationFrame(go);
    setTimeout(go, 120);
    setTimeout(go, 400);
  }

  // Jasper confirms in his own words — a text bubble, not a status line — and
  // asks what's next. The figures come from the account API's response.
  function jasperSays(text, opts) {
    var msg = { role: 'assistant', text: text, actions: [], cards: [] };
    if (opts && opts.csat) msg.csat = true;
    pushMessage(msg);
    var bubble = renderMessage(msg);
    playFace();
    revealBottom();
    return bubble;
  }

  // ---- CSAT: one question, once, at the moment of resolution -----------------
  // Jon, 2026-09-16: "ask if we solved their problem." The question IS the
  // signal — no survey. Asked (a) as the tail of Jasper's confirmation after an
  // in-chat action, (b) after an answered question once the customer has been
  // quiet for a bit. The first tap is recorded (jr-help `feedback`) and never
  // asked again in this conversation; no tap is a third state, not a hole.
  var CSAT_Q = 'Did that take care of what you needed?';
  var csatTimer = null;
  function csatAnswered() { return readJSON(KEY.csat) === sessionId; }
  function csatAskedAlready() { return csatAnswered() || transcript.some(function (m) { return m && m.csat; }); }
  function clearCsatTimer() { if (csatTimer) { clearTimeout(csatTimer); csatTimer = null; } }
  function csatButtons() {
    var wrap = el('div', 'jr-help__actions jr-help__csat');
    wrap.appendChild(btn('jr-help__action', 'Yes, all set', function () { answerCsat('solved', wrap); }));
    wrap.appendChild(btn('jr-help__action', 'Not quite', function () { answerCsat('not_solved', wrap); }));
    list.appendChild(wrap);
    revealBottom();
  }
  function answerCsat(verdict, wrap) {
    track('csat_answered', { verdict: verdict });
    if (wrap && wrap.parentNode) wrap.parentNode.removeChild(wrap);
    writeJSON(KEY.csat, sessionId);
    pushMessage({ role: 'csat', verdict: verdict });
    fetch(CFG.endpoint, { method: 'POST', headers: headers(), body: JSON.stringify({ action: 'feedback', session_id: sessionId, verdict: verdict }) }).catch(function () {});
    if (verdict === 'solved') {
      jasperSays('Glad to hear it — I’m here if anything else comes up.');
    } else {
      jasperSays('Tell me what’s still not right and I’ll keep at it — or I can get a specialist on it.');
      setTimeout(function () { input.focus(); }, 0);
    }
  }
  // Ask on its own, after a quiet spell following an answered question.
  function armCsatAfterAnswer() {
    clearCsatTimer();
    if (csatAskedAlready()) return;
    csatTimer = setTimeout(function () {
      csatTimer = null;
      if (!isOpen() || busy || csatAskedAlready() || (input.value || '').trim()) return;
      track('csat_asked', { trigger: 'quiet' });
      jasperSays(CSAT_Q, { csat: true });   // renderMessage adds the two taps
    }, 45000);
  }

  function openActionCard(a) {
    closeConfirm();
    var card = el('div', 'jr-help__confirm');
    var cbody = el('div', 'jr-help__confirm-body');
    var row = el('div', 'jr-help__confirm-row');
    card.appendChild(cbody); card.appendChild(row);
    list.appendChild(card);
    // revealBottom() runs at the END of this function, once the card has its
    // text and buttons — scrolling before that measured an empty card and left
    // the buttons below the frame.

    var subId = String(a.subscription_id);
    function busy(on) { [].forEach.call(row.querySelectorAll('button'), function (b) { b.disabled = on; }); }
    function fail(err) {
      track('action_failed', { kind: a.kind, error: String((err && err.message) || 'unknown').slice(0, 80) });
      closeConfirm();
      jasperSays('That didn’t go through on my end (' + String((err && err.message) || 'unknown error') + '). Nothing changed. You can make the change from your account page, or tell me and I’ll try again.');
      renderActions([{ kind: 'open_order', label: 'Open my account' }]);
      revealBottom();
    }
    var offer = a.detail && /^offer:/.test(a.detail) ? a.detail.replace(/^offer:/, '') : null;
    function done(text) {
      track('action_confirmed', { kind: a.kind, offer: offer });
      if (offer) {
        // Taking the offer = kept, in the flow's own vocabulary.
        track('cancel_flow_offer_accepted', { offer: offer, reason: lastCancel && lastCancel.cause || null });
        track('cancel_flow_outcome', { outcome: 'kept', via: 'offer', offer: offer, locus: lastCancel && lastCancel.locus || null, cause: lastCancel && lastCancel.cause || null, verbatim: lastCancel && lastCancel.verbatim || null });
      }
      closeConfirm();
      if (csatAskedAlready()) { jasperSays(text); return; }
      jasperSays(text + ' ' + CSAT_Q, { csat: true });   // renderMessage adds the two taps
    }
    var never = btn('jr-help__btn jr-help__btn--ghost', 'Not now', function () { track('action_cancelled', { kind: a.kind, offer: offer }); if (offer) track('cancel_flow_offer_declined', { offer: offer }); closeConfirm(); });

    if (a.kind === 'reschedule' && offer === 'push_6_months') {
      var six = new Date(); six.setMonth(six.getMonth() + 6);
      var sixIso = six.toISOString().slice(0, 10);
      cbody.textContent = 'Push your next shipment out to ' + fmtDate(sixIso) + '? Nothing ships or charges until then, and you can move it again any time.';
      row.appendChild(btn('jr-help__btn', 'Push it out', function () {
        busy(true);
        callAccount({ action: 'reschedule', subscription_id: subId, date: sixIso })
          .then(function (res) { done('Done — your next shipment is now ' + (fmtDate(res.next_charge_at) || fmtDate(sixIso)) + '. Your subscription stays exactly as it is until then.'); })
          .catch(fail);
      }));
      row.appendChild(never);
    } else if (a.kind === 'change_frequency' && offer === 'stretch_to_6_months') {
      cbody.textContent = 'Ship every 6 months instead? Your next shipment date moves to fit the new rhythm; the confirmation shows the new one.';
      row.appendChild(btn('jr-help__btn', 'Every 6 months', function () {
        busy(true);
        callAccount({ action: 'set_frequency', subscription_id: subId, unit: 'month', frequency: 6 })
          .then(function (res) { done('Done — you’re now shipping every 6 months.' + nextDateNote(res) + ' Say the word if you’d rather it landed on a different day.'); })
          .catch(fail);
      }));
      row.appendChild(never);
    } else if (a.kind === 'skip_next') {
      cbody.textContent = 'Skip the next shipment of this subscription? The one after it stays on schedule.';
      row.appendChild(btn('jr-help__btn', 'Skip it', function () {
        busy(true);
        callAccount({ action: 'skip', subscription_id: subId })
          .then(function (res) {
            var d = fmtDate(res.next_charge_at);
            done('Done — that shipment is skipped' + (d ? ', and your next one is now ' + d : '') + '. Your subscription stays active and nothing else changes.');
          })
          .catch(fail);
      }));
      row.appendChild(never);
    } else if (a.kind === 'reactivate') {
      cbody.textContent = 'Restart this subscription? It picks up on its usual cadence and you can change the date afterwards.';
      row.appendChild(btn('jr-help__btn', 'Restart it', function () {
        busy(true);
        callAccount({ action: 'reactivate', subscription_id: subId })
          .then(function () { done('Done — your subscription is active again on its usual cadence. If you’d like the next shipment on a particular date, just say so.'); })
          .catch(fail);
      }));
      row.appendChild(never);
    } else if (a.kind === 'reschedule') {
      cbody.textContent = 'Pick the new shipment date.';
      var date = el('input', 'jr-help__date');
      date.type = 'date';
      var min = new Date(); min.setDate(min.getDate() + 1);
      date.min = min.toISOString().slice(0, 10);
      // Pre-fill the date Jasper named (detail "date:YYYY-MM-DD"); the picker
      // opened empty on 9/20 after "Move next shipment to Dec 19".
      var pre = /(?:^|\s)date:(\d{4}-\d{2}-\d{2})/.exec(String(a.detail || ''));
      if (pre && pre[1] >= date.min) date.value = pre[1];
      cbody.appendChild(date);
      row.appendChild(btn('jr-help__btn', 'Move it', function () {
        if (!date.value) { date.focus(); return; }
        busy(true);
        callAccount({ action: 'reschedule', subscription_id: subId, date: date.value })
          .then(function (res) { done('Done — your next shipment is moved to ' + (fmtDate(res.next_charge_at) || date.value) + '.'); })
          .catch(fail);
      }));
      row.appendChild(never);
      setTimeout(function () { date.focus(); }, 0);
    } else if (a.kind === 'change_frequency') {
      cbody.textContent = 'Loading your options…';
      callAccount({ action: 'frequency_options', subscription_id: subId }).then(function (res) {
        var opts = (res && res.options) || [];
        if (!opts.length) throw new Error('no options');
        cbody.textContent = 'Ship every:';
        var choices = el('div', 'jr-help__choices');
        var picked = res.current ? (res.current.unit + ':' + res.current.frequency) : '';
        opts.forEach(function (o) {
          var k = o.unit + ':' + o.frequency;
          // "Every month" → "1 month" so the chips read as a series.
          var text = String(o.label || '').replace(/^Every\s+/i, '');
          if (!/^\d/.test(text)) text = '1 ' + text;
          var c = btn('jr-help__chip' + (k === picked ? ' jr-help__chip--on' : ''), text, function () {
            picked = k;
            [].forEach.call(choices.querySelectorAll('.jr-help__chip'), function (x) { x.classList.remove('jr-help__chip--on'); });
            c.classList.add('jr-help__chip--on');
          });
          choices.appendChild(c);
        });
        cbody.appendChild(choices);
        row.appendChild(btn('jr-help__btn', 'Save', function () {
          if (!picked) return;
          var bits = picked.split(':');
          var label = ''; opts.forEach(function (o) { if (o.unit + ':' + o.frequency === picked) label = String(o.label || '').replace(/^Every\s+/i, ''); });
          if (label && !/^\d/.test(label)) label = '1 ' + label;
          busy(true);
          callAccount({ action: 'set_frequency', subscription_id: subId, unit: bits[0], frequency: parseInt(bits[1], 10) })
            .then(function (res) { done('Done — you’re now shipping every ' + (label || bits[1] + ' ' + bits[0]) + '.' + nextDateNote(res) + ' Say the word if you’d rather it landed on a different day.'); })
            .catch(fail);
        }));
        row.appendChild(never);
        revealBottom();
      }).catch(fail);
    }
    revealBottom();
  }

  // ---- Add to cart, from the chat ---------------------------------------------
  // Jon, 2026-09-17: "If Jasper is having that kind of conversation he should
  // offer the add to cart function within the chat." One tap: the pill carries
  // the exact variant (and selling plan, for a subscription) the backend copied
  // from the catalog record, the storefront's own /cart/add.js does the add,
  // Jasper confirms in his own words and the header count refreshes
  // (`cart:build` is the theme's own rebuild hook — it updates the drawer's
  // contents and count without opening it over the chat). The cart also gets a
  // `jr_help_session` attribute so the order, if it comes, is attributable to
  // this conversation.
  function addToCart(a, pill) {
    if (!a || !a.variant_id) return;
    track('action_clicked', { kind: 'add_to_cart', plan: !!a.selling_plan_id });
    // The brain sets quantity when he asked for more than one ("make it two");
    // 1 otherwise, never more than 10 — the same cap the server applies.
    var qty = Math.floor(Number(a.quantity));
    var item = { id: Number(a.variant_id), quantity: qty >= 1 ? Math.min(qty, 10) : 1 };
    if (a.selling_plan_id) item.selling_plan = Number(a.selling_plan_id);
    if (pill) { pill.disabled = true; pill.classList.add('jr-help__action--busy'); }
    fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [item], attributes: { jr_help_session: sessionId } })
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok || j.status === 422 || j.message === 'Cart Error') throw new Error((j && (j.description || j.message)) || ('http ' + r.status));
        return j;
      });
    }).then(function (j) {
      var added = (j && j.items && j.items[0]) || j || {};
      var title = added.product_title || added.title || 'That';
      var plan = added.selling_plan_allocation && added.selling_plan_allocation.selling_plan && added.selling_plan_allocation.selling_plan.name;
      track('cart_added', { variant_id: a.variant_id, plan: !!a.selling_plan_id });
      try { document.dispatchEvent(new CustomEvent('cart:build')); } catch (e) {}
      try { fetch('/cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { jr_help_session: sessionId } }) }).catch(function () {}); } catch (e) {}
      if (pill) { pill.disabled = false; pill.classList.remove('jr-help__action--busy'); pill.textContent = 'Added ✓'; pill.classList.add('jr-help__action--done'); }
      var line = plan ? 'Done — ' + title + ' is in your cart as a subscription (' + plan.toLowerCase().replace(/ with .*$/, '') + '), charged each time it ships until you cancel. '
                      : 'Done — ' + title + ' is in your cart. ';
      var msg = { role: 'assistant', text: line + 'Anything else before you check out?', actions: [{ kind: 'view_cart', label: 'View cart' }], cards: [] };
      pushMessage(msg);
      renderMessage(msg);
      playFace();
      revealBottom();
    }).catch(function (err) {
      track('cart_failed', { error: String((err && err.message) || 'unknown').slice(0, 80) });
      if (pill) { pill.disabled = false; pill.classList.remove('jr-help__action--busy'); }
      jasperSays('That didn’t make it into your cart (' + String((err && err.message) || 'unknown error') + '). Nothing was added — you can try the button again or add it from the product page.');
    });
  }

  // ---- Cancellation: the split test's two arms ------------------------------
  // Jon, 2026-09-17: "test a flow in chat rather than pushing it to our current
  // flow… keep them the same." The arm is the backend's coin flip on the
  // customer id (meta event). Both arms make the same offers and write the
  // same cancel_flow_* events; the chat arm's reason is what he TOLD Jasper.
  var RX_OPTIONS = [
    ['one_time_option', 'A way to buy without a subscription'], ['less_often', 'Shipping less often'], ['lower_price', 'A lower price'],
    ['better_product', 'A better product'], ['fixed_issue', 'Fixing what went wrong'], ['different_product', 'A different product'],
    ['faster_support', 'Faster help'], ['nothing', 'Nothing — I’m done'],
  ];
  function subFromCards(subId) {
    for (var i = transcript.length - 1; i >= 0; i--) {
      var cs = transcript[i] && transcript[i].cards || [];
      for (var j = 0; j < cs.length; j++) if (cs[j].kind === 'subscription' && String(cs[j].subscription_id) === String(subId)) return cs[j];
    }
    return null;
  }
  function askPrescription() {
    var msg = { role: 'assistant', text: 'One last thing, no strings attached — what would have kept you?', actions: [], cards: [] };
    pushMessage(msg); renderMessage(msg);
    var wrap = el('div', 'jr-help__actions');
    RX_OPTIONS.forEach(function (o) {
      wrap.appendChild(btn('jr-help__action', o[1], function () {
        track('cancel_flow_prescription', { would_have_kept: o[0], locus: lastCancel && lastCancel.locus || null, cause: lastCancel && lastCancel.cause || null });
        if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
        pushMessage({ role: 'customer', text: o[1] }); renderMessage({ role: 'customer', text: o[1] });
        if (csatAskedAlready()) { jasperSays('Noted — thank you. I’m here if you ever want to come back.'); return; }
        jasperSays('Noted — thank you. ' + CSAT_Q, { csat: true });
      }));
    });
    list.appendChild(wrap);
    revealBottom();
  }
  function cancelInChat(a) {
    var subId = String(a.subscription_id);
    if (cxVariant === 'flow' || !cxVariant) { openFlowOverlay(a); return; }
    closeConfirm();
    var oneTime = a.detail === 'offer:convert_to_one_time';
    var card = el('div', 'jr-help__confirm');
    var cbody = el('div', 'jr-help__confirm-body');
    var row = el('div', 'jr-help__confirm-row');
    card.appendChild(cbody); card.appendChild(row); list.appendChild(card);
    cbody.textContent = oneTime
      ? 'Cancel the subscription and buy one-time from now on? You’ll only be charged when you order.'
      : 'Cancel this subscription? Nothing else on your account changes, and you can restart it any time.';
    function busy(on) { [].forEach.call(row.querySelectorAll('button'), function (b) { b.disabled = on; }); }
    row.appendChild(btn('jr-help__btn', oneTime ? 'Cancel & go one-time' : 'Cancel it', function () {
      busy(true);
      var reason = oneTime ? 'Converted to one-time' : ((lastCancel && (lastCancel.cause || lastCancel.locus)) || 'No reason given');
      callAccount({ action: 'cancel', subscription_id: subId, reason: reason })
        .then(function () {
          track('cancel_flow_outcome', { outcome: oneTime ? 'converted_one_time' : 'cancelled', locus: lastCancel && lastCancel.locus || null, cause: lastCancel && lastCancel.cause || null, verbatim: lastCancel && lastCancel.verbatim || null, via: 'jasper' });
          closeConfirm();
          if (oneTime) {
            jasperSays('Done — the subscription is cancelled, and you can buy one-time from any product page whenever you like.');
            renderActions([{ kind: 'open_order', label: 'Open my account' }]);
            askPrescription();
          } else {
            jasperSays('Done — that subscription is cancelled. Nothing else on your account changes, and you can restart it any time from your account page.');
            askPrescription();
          }
        })
        .catch(function (err) {
          track('action_failed', { kind: 'cancel_subscription', error: String((err && err.message) || 'unknown').slice(0, 80) });
          closeConfirm();
          jasperSays('That didn’t go through on my end (' + String((err && err.message) || 'unknown error') + '). Nothing changed — you can cancel from your account page, or tell me and I’ll try again.');
          renderActions([{ kind: 'cancel_subscription', label: 'Cancel my subscription', subscription_id: subId }, { kind: 'open_order', label: 'Open my account' }]);
        });
    }));
    row.appendChild(btn('jr-help__btn jr-help__btn--ghost', 'Not now', function () { track('cancel_flow_abandoned', { at_step: 'confirm', via: 'not_now' }); closeConfirm(); }));
    revealBottom();
  }
  function offerSupportHandoff(a) {
    var subId = String(a.subscription_id);
    closeConfirm();
    var card = el('div', 'jr-help__confirm');
    var cbody = el('div', 'jr-help__confirm-body');
    var row = el('div', 'jr-help__confirm-row');
    card.appendChild(cbody); card.appendChild(row); list.appendChild(card);
    cbody.textContent = 'Send what you told me to a person on our team? They’ll reply by email, and your subscription stays as it is meanwhile.';
    var verbatim = (lastCancel && lastCancel.verbatim) || '';
    row.appendChild(btn('jr-help__btn', 'Send it', function () {
      [].forEach.call(row.querySelectorAll('button'), function (b) { b.disabled = true; });
      callAccount({ action: 'support_request', subscription_id: subId, topic: (lastCancel && lastCancel.cause) || 'other', message: verbatim || 'Customer asked Jasper for help before cancelling (no further detail given).' })
        .then(function () {
          track('cancel_flow_offer_accepted', { offer: 'support_handoff', reason: lastCancel && lastCancel.cause || null });
          track('cancel_flow_outcome', { outcome: 'kept', via: 'offer', offer: 'support_handoff', locus: lastCancel && lastCancel.locus || null, cause: lastCancel && lastCancel.cause || null, verbatim: verbatim || null });
          closeConfirm();
          jasperSays('Done — a person on our team has your message and will reply by email. Your subscription is unchanged until you say otherwise.' + (csatAskedAlready() ? '' : ' ' + CSAT_Q), csatAskedAlready() ? undefined : { csat: true });
        })
        .catch(function (err) { closeConfirm(); jasperSays('That didn’t send (' + String((err && err.message) || 'unknown error') + '). Email support@jeremysrazors.com and we’ll pick it up there.'); });
    }));
    row.appendChild(btn('jr-help__btn jr-help__btn--ghost', 'Not now', function () { track('cancel_flow_offer_declined', { offer: 'support_handoff' }); closeConfirm(); }));
    revealBottom();
  }
  // The flow arm: today's guided overlay, opened right here. Its assets ride
  // the account page/drawer, not every page, so they load on first use.
  function ensureFlow() {
    if (window.JRCancelFlow) return Promise.resolve();
    if (!CFG.cancelFlowJs) return Promise.reject(new Error('flow unavailable'));
    return new Promise(function (resolve, reject) {
      if (CFG.cancelFlowCss && !document.querySelector('link[href="' + CFG.cancelFlowCss + '"]')) { var l = document.createElement('link'); l.rel = 'stylesheet'; l.href = CFG.cancelFlowCss; document.head.appendChild(l); }
      var sc = document.createElement('script'); sc.src = CFG.cancelFlowJs; sc.onload = function () { window.JRCancelFlow ? resolve() : reject(new Error('flow failed to load')); }; sc.onerror = function () { reject(new Error('flow failed to load')); };
      document.head.appendChild(sc);
    });
  }
  function openFlowOverlay(a) {
    var subId = String(a.subscription_id);
    var card = subFromCards(subId) || {};
    ensureFlow().then(function () {
      track('cancel_handoff', { to: 'overlay' });
      // The overlay must be ON TOP: the floating panel (z 10011) otherwise
      // covers it (Jon, 2026-09-17: "the jasper box is still hovering over
      // it… even worse on mobile"). Step the panel behind the overlay for as
      // long as the overlay exists, then bring it back — whichever way the
      // overlay closed. On a phone the sheet also drops its scroll lock so the
      // overlay scrolls.
      root.classList.add('jr-help--behind');
      document.documentElement.classList.remove('jr-help-locked');
      var restore = function () {
        root.classList.remove('jr-help--behind');
        if (isOpen() && isPhone() && !inline) document.documentElement.classList.add('jr-help-locked');
        revealBottom();
      };
      var watch = setInterval(function () {
        if (!document.querySelector('.jr-cx-overlay')) { clearInterval(watch); restore(); }
      }, 400);
      setTimeout(function () { if (!document.querySelector('.jr-cx-overlay')) { clearInterval(watch); restore(); } }, 1500);
      window.JRCancelFlow.open({
        surface: 'chat',
        sub: { id: subId, title: card.product || '', interval: card.every || '', nextAt: card.next_charge_at || null, priceText: typeof card.price_usd === 'number' ? fmtMoney(card.price_usd) : '' },
        portrait: CFG.portraitUrl || '', oneTimeUrl: '/collections/all', call: callAccount,
        onCancelled: function () { jasperSays('Done — that subscription is cancelled. You can restart it any time from your account page.' + (csatAskedAlready() ? '' : ' ' + CSAT_Q), csatAskedAlready() ? undefined : { csat: true }); },
        onKept: function (offer, res) {
          var text = offer === 'support_handoff' ? 'Done — a person on our team has your message and will reply by email.'
                   : res && res.next_charge_at ? 'Done — your next shipment is now ' + fmtDate(res.next_charge_at) + '.'
                   : 'Done — your subscription stays as it is.';
          jasperSays(text + (csatAskedAlready() ? '' : ' ' + CSAT_Q), csatAskedAlready() ? undefined : { csat: true });
        }
      });
    }).catch(function () {
      // No overlay available: fall back to the conversation's own confirm card.
      cxVariant = 'chat'; cancelInChat(a);
    });
  }

  // `escalate` is not a pill — the backend already recorded it, so it renders
  // as a status line. At most three pills; the backend orders them.
  function renderActions(actions, fresh) {
    var wrap = el('div', 'jr-help__actions');
    var shown = 0;
    for (var i = 0; i < actions.length && shown < 3; i++) {
      var a = actions[i];
      if (!a || !a.kind || a.kind === 'escalate') continue;
      var label = a.label || a.kind.replace(/_/g, ' ');
      if (a.kind === 'add_to_cart') {
        if (!a.variant_id) continue;
        // A pill that adds a SUBSCRIPTION says so before it is pressed, whatever
        // label the backend sent — the shopper never chose a plan in a chat the
        // way they do on a product page (Jon, 2026-09-17).
        if (a.selling_plan_id && !/subscri/i.test(label)) label = label + ' (subscription)';
        var cartPill = btn('jr-help__action jr-help__action--cart', label, (function (act) { return function () { addToCart(act, this); }; })(a));
        wrap.appendChild(cartPill);
        shown++;
        continue;
      }
      if (a.kind === 'view_cart') {
        var cartLink = el('a', 'jr-help__action', label || 'View cart');
        cartLink.href = '/cart';
        cartLink.addEventListener('click', function () { writeJSON(KEY.open, false); track('action_clicked', { kind: 'view_cart', in_chat: false }); flushEvents(true); });
        wrap.appendChild(cartLink);
        shown++;
        continue;
      }
      // The cancellation conversation's buttons: the cancel itself, and the
      // offers Jasper made (detail "offer:<key>" — the same keys the guided
      // flow uses, so the two arms write the same events).
      if (a.kind === 'cancel_subscription' && a.subscription_id && CFG.loggedIn) {
        wrap.appendChild(btn('jr-help__action', label, (function (act) { return function () { track('action_clicked', { kind: 'cancel_subscription', in_chat: true, variant: cxVariant }); cancelInChat(act); }; })(a)));
        shown++;
        continue;
      }
      if (a.kind === 'escalate' && a.detail === 'offer:support_handoff' && a.subscription_id && CFG.loggedIn) {
        wrap.appendChild(btn('jr-help__action', label, (function (act) { return function () { track('action_clicked', { kind: 'support_handoff', in_chat: true }); offerSupportHandoff(act); }; })(a)));
        shown++;
        continue;
      }
      if (IN_CHAT[a.kind] && a.subscription_id && CFG.loggedIn) {
        wrap.appendChild(btn('jr-help__action', label, (function (act) { return function () { track('action_clicked', { kind: act.kind, in_chat: true, offer: act.detail || null }); openActionCard(act); }; })(a)));
        shown++;
        continue;
      }
      var href = actionHref(a);
      if (!href) continue;
      var pill = el('a', 'jr-help__action', label);
      pill.href = href;
      pill.addEventListener('click', (function (kind) { return function () {
        // They asked to go somewhere: don't re-open over the destination —
        // except sign-in, which comes back to THIS page and should resume.
        writeJSON(KEY.open, kind === 'sign_in');
        track(kind === 'sign_in' ? 'signin_clicked' : 'action_clicked', { kind: kind, in_chat: false });
        flushEvents(true);
      }; })(a.kind));
      wrap.appendChild(pill);
      shown++;
    }
    if (shown) { list.appendChild(wrap); if (fresh) track('action_shown', { kinds: actions.slice(0, 3).map(function (a) { return a && a.kind; }) }); }
  }

  // Anonymous shoppers choose first: sign in, or carry on as a guest. The
  // choice is remembered so a reload doesn't ask again; sending a message
  // counts as choosing guest.
  var signin = $('JrHelpSignin');
  var signinBtn = $('JrHelpSigninBtn');
  if (signinBtn) signinBtn.href = signInHref();
  if (signinBtn) signinBtn.addEventListener('click', function () { writeJSON(KEY.open, true); track('signin_clicked', { from: 'greeting' }); flushEvents(true); });
  function guestChosen() { return CFG.loggedIn || readJSON(KEY.guest) === true; }

  function renderTranscript() {
    list.innerHTML = '';
    for (var i = 0; i < transcript.length; i++) renderMessage(transcript[i]);
    var any = transcript.length > 0;
    var chosen = guestChosen() || any;
    if (signin) signin.hidden = chosen;
    if (chips) chips.hidden = any || !chosen;
    if (welcome) welcome.classList.toggle('jr-help__welcome--compact', any);
  }

  function pushMessage(msg) {
    touch();
    transcript.push(msg);
    if (transcript.length > TRANSCRIPT_CAP) transcript = transcript.slice(-TRANSCRIPT_CAP);
    writeJSON(KEY.transcript, transcript);
  }

  function showError(text) {
    var row = el('div', 'jr-help__msg jr-help__msg--assistant jr-help__msg--error');
    row.appendChild(avatar());
    row.appendChild(el('div', 'jr-help__bubble', text));
    list.appendChild(row);
    scrollToBottom(true);
  }

  function typingIndicator() {
    var t = el('span', 'jr-help__typing');
    t.setAttribute('aria-label', 'Jasper is typing');
    for (var i = 0; i < 3; i++) t.appendChild(el('i'));
    return t;
  }

  // ---- Open / close ------------------------------------------------------
  function isOpen() { return root.getAttribute('data-open') === 'true'; }
  function isPhone() { return window.matchMedia && window.matchMedia('(max-width: 600px)').matches; }

  function syncViewportHeight() {
    // iOS keeps the layout viewport tall under the keyboard; the visual
    // viewport is what actually shrinks. Feed it to CSS so the composer stays
    // visible while typing on a phone.
    var vv = window.visualViewport;
    var h = (vv && vv.height) ? vv.height : window.innerHeight;
    root.style.setProperty('--jr-help-vh', Math.round(h) + 'px');
  }

  function open(fromUser) {
    if (isOpen()) return;
    lastFocus = fromUser ? document.activeElement : null;
    panel.hidden = false;
    // Force a layout between un-hiding and flipping the state so the CSS
    // transition runs from the hidden state. Done synchronously (not via
    // requestAnimationFrame) so `isOpen()` is true immediately — rAF stalls in
    // a background tab, and a reply finishing then would be mis-marked unread.
    void panel.offsetWidth;
    root.setAttribute('data-open', 'true');
    launcher.setAttribute('aria-expanded', 'true');
    root.setAttribute('data-unread', 'false');
    remove(KEY.unread);
    writeJSON(KEY.open, true);
    if (isPhone() && !inline) document.documentElement.classList.add('jr-help-locked');
    syncViewportHeight();
    if (window.visualViewport) window.visualViewport.addEventListener('resize', syncViewportHeight);
    renderTranscript();
    scrollToBottom(true);
    playFace();
    if (fromUser) setTimeout(function () { input.focus(); }, reduceMotion() ? 0 : 180);
    if (!pinged) { pinged = true; ping(); }
    if (fromUser) track('opened', { turns: transcript.filter(function (m) { return m.role === 'customer'; }).length, signed_in: !!CFG.loggedIn });
  }

  function close() {
    if (!isOpen() || inline) return;
    track('closed', { turns: transcript.filter(function (m) { return m.role === 'customer'; }).length, csat_pending: !!list.querySelector('.jr-help__csat') });
    root.setAttribute('data-open', 'false');
    launcher.setAttribute('aria-expanded', 'false');
    writeJSON(KEY.open, false);
    document.documentElement.classList.remove('jr-help-locked');
    if (window.visualViewport) window.visualViewport.removeEventListener('resize', syncViewportHeight);
    var done = function () { if (!isOpen()) panel.hidden = true; };
    if (reduceMotion()) done(); else setTimeout(done, 170);
    if (lastFocus && lastFocus.focus) lastFocus.focus(); else launcher.focus();
    lastFocus = null;
  }

  launcher.addEventListener('click', function () { isOpen() ? close() : open(true); });
  launcher.addEventListener('mouseenter', playFace);
  // First paint: play once, like the console does on load.
  if (document.readyState === 'complete') playFace(); else window.addEventListener('load', playFace);
  if (closeBtn) closeBtn.addEventListener('click', close);

  // Escape closes; Tab wraps inside the dialog so keyboard users don't fall
  // out behind the sheet on a phone.
  panel.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    var focusables = panel.querySelectorAll('button:not([disabled]), a[href], textarea:not([disabled]), input:not([disabled])');
    if (!focusables.length) return;
    var first = focusables[0], last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  // ---- Composer ----------------------------------------------------------
  function autoGrow() {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 120) + 'px';
  }
  input.addEventListener('input', function () { autoGrow(); clearCsatTimer(); });
  input.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      form.requestSubmit ? form.requestSubmit() : submit();
    }
  });
  form.addEventListener('submit', function (e) { e.preventDefault(); submit(); });

  function submit() {
    var text = (input.value || '').replace(/\s+$/, '');
    if (!text || busy) return;
    if (text.length > MAX_MESSAGE) text = text.slice(0, MAX_MESSAGE);
    input.value = '';
    autoGrow();
    send(text);
  }

  if (chips) {
    chips.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-chip]');
      if (!btn) return;
      track('chip_clicked', { chip: btn.textContent.trim().slice(0, 40) });
      if (btn.getAttribute('data-chip') === 'focus') { input.focus(); return; }
      send(btn.textContent.trim());
    });
  }
  if (signin) {
    signin.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-chip="guest"]');
      if (!btn) return;
      writeJSON(KEY.guest, true);
      track('guest_chosen');
      renderTranscript();
      input.focus();
    });
  }

  function setBusy(on) {
    busy = on;
    input.disabled = on;
    if (sendBtn) sendBtn.disabled = on;
    if (!on) input.focus();
  }

  // ---- Page context ----------------------------------------------------------
  // What the customer is looking at, so "this one" means this one. The
  // product/collection handles come from Liquid (the page's own objects); the
  // backend resolves them against the catalog mirror — the browser's word for
  // a product is a hint, never the fact.
  function pageContext() {
    var pg = (CFG.page && typeof CFG.page === 'object') ? CFG.page : {};
    var out = { path: location.pathname, title: document.title, type: pg.type || null };
    if (pg.product && pg.product.handle) out.product = { handle: pg.product.handle, title: pg.product.title || null };
    if (pg.collection && pg.collection.handle) out.collection = { handle: pg.collection.handle, title: pg.collection.title || null };
    return out;
  }

  // ---- Analytics ---------------------------------------------------------------
  // Every meaningful thing the customer does with the chat, batched to the
  // backend's `events` action (fixed names, per-session sequence so a retry
  // never double-counts). Flushed after a short delay, and on pagehide via
  // sendBeacon so "they left mid-answer" is recordable. Never the message
  // text — the conversation log already has that.
  var evQueue = [];
  var evTimer = null;
  var evSeq = Number(readJSON('jrhelp:evseq:' + sessionId)) || 0;
  function track(name, props) {
    evQueue.push({ name: name, seq: evSeq++, props: props || {} });
    writeJSON('jrhelp:evseq:' + sessionId, evSeq);
    if (evQueue.length >= 10) flushEvents(false);
    else if (!evTimer) evTimer = setTimeout(function () { flushEvents(false); }, 2500);
  }
  function flushEvents(unloading) {
    if (evTimer) { clearTimeout(evTimer); evTimer = null; }
    if (!evQueue.length) return;
    var batch = evQueue.splice(0, 20);
    var tok = readJSON(KEY.token);
    var body = JSON.stringify({ action: 'events', session_id: sessionId, events: batch, page: { path: location.pathname }, customer_token: tok && tok.token ? tok.token : undefined });
    if (unloading && navigator.sendBeacon) {
      // sendBeacon cannot set headers, so the preview code rides in the body
      // is not an option; on a gated backend these are dropped, which is fine
      // for a preview.
      try { navigator.sendBeacon(CFG.endpoint, new Blob([body], { type: 'text/plain' })); return; } catch (e) {}
    }
    try {
      fetch(CFG.endpoint, { method: 'POST', headers: headers(), body: body, keepalive: true }).catch(function () {});
    } catch (e) {}
    if (evQueue.length) flushEvents(unloading);
  }
  var pendingReply = false;   // a question is in flight — leaving now is an abandonment
  window.addEventListener('pagehide', function () {
    if (pendingReply) track('abandoned', { while: 'waiting_for_reply' });
    flushEvents(true);
  });

  // ---- Token bridge ------------------------------------------------------
  function getCustomerToken() {
    if (!CFG.loggedIn) return Promise.resolve(null);
    var cached = readJSON(KEY.token);
    var now = Date.now() / 1000;
    if (cached && cached.token && typeof cached.exp === 'number' && cached.exp - now > 60) {
      return Promise.resolve(cached.token);
    }
    // Trailing slash matches the account UI's proven App Proxy path.
    return fetch('/apps/jr/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ action: 'help_token' })
    }).then(function (r) {
      if (!r.ok) return null;
      return r.json().catch(function () { return null; });
    }).then(function (j) {
      if (j && j.ok && j.token) {
        writeJSON(KEY.token, { token: j.token, exp: Number(j.exp) || (now + 300) });
        return j.token;
      }
      return null;
    }).catch(function () { return null; });
  }

  // ---- Backend calls -----------------------------------------------------
  var previewCode = readLocal(KEY.preview);

  function headers() {
    var h = { 'Content-Type': 'application/json' };
    if (previewCode) h['x-help-preview'] = previewCode;
    return h;
  }

  // ---- Preview gate --------------------------------------------------------
  function showAccess(errored) {
    if (!access) return;
    form.hidden = true;
    access.hidden = false;
    access.classList.toggle('jr-help__access--error', !!errored);
    if (accessText) accessText.textContent = errored
      ? 'That code didn’t work. Check it and try again.'
      : 'This chat is in preview. Enter the preview code to try it.';
    if (accessBtn) accessBtn.disabled = false;
    setTimeout(function () { if (accessInput) accessInput.focus(); }, 0);
  }
  function hideAccess() {
    if (access) access.hidden = true;
    form.hidden = false;
  }

  // ping → {ok, preview, open}. `open` is false while the backend is gated and
  // the header we sent (if any) did not match. Resolves true when chat may proceed.
  function ping() {
    return fetch(CFG.endpoint, { method: 'POST', headers: headers(), body: JSON.stringify({ action: 'ping' }) })
      .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
      .then(function (j) {
        if (!j || !j.ok) throw new Error('not ok');
        if (j.preview && !j.open) {
          // A stored code that no longer opens the gate is stale: drop it.
          var stale = !!previewCode;
          previewCode = ''; writeLocal(KEY.preview, '');
          showAccess(stale);
          return false;
        }
        if (!j.preview) { previewCode = ''; writeLocal(KEY.preview, ''); }
        hideAccess();
        return true;
      })
      .catch(function () { showError(ERROR_COPY); return false; });
  }

  if (access) {
    access.addEventListener('submit', function (e) {
      e.preventDefault();
      var code = (accessInput && accessInput.value || '').trim();
      if (!code) return;
      if (accessBtn) accessBtn.disabled = true;
      previewCode = code; writeLocal(KEY.preview, code);
      ping().then(function (ok) {
        if (ok) { track('preview_unlocked'); if (accessInput) accessInput.value = ''; setTimeout(function () { input.focus(); }, 0); }
      });
    });
  }

  function historyForRequest() {
    var out = [];
    for (var i = transcript.length - 1; i >= 0 && out.length < HISTORY_TURNS; i--) {
      var m = transcript[i];
      if (m.role !== 'customer' && m.role !== 'assistant') continue;
      out.unshift({ role: m.role, text: m.text });
    }
    return out;
  }

  function send(text) {
    if (busy) return;
    clearCsatTimer();
    setBusy(true);
    userScrolledUp = false;
    if (chips) chips.hidden = true;
    if (welcome) welcome.classList.add('jr-help__welcome--compact');

    // Typing to Jasper is choosing to carry on (as a guest, if not signed in).
    if (!guestChosen()) writeJSON(KEY.guest, true);
    // A conversation that STARTS signed in is already the customer's: mark it
    // claimed so the return-from-sign-in note ("you're signed in — I can see
    // your orders now") never appears mid-thread on a later page load.
    if (CFG.loggedIn) writeJSON(KEY.claimed, sessionId);
    if (signin) signin.hidden = true;
    var sentAt = Date.now(), firstWordAt = null;
    pendingReply = true;
    track('message_sent', { chars: text.length, turn: transcript.filter(function (m) { return m.role === 'customer'; }).length + 1, signed_in: !!CFG.loggedIn, page_type: (CFG.page && CFG.page.type) || null });
    // History must be captured BEFORE this message is pushed.
    var history = historyForRequest();
    pushMessage({ role: 'customer', text: text });
    renderMessage({ role: 'customer', text: text });

    // Typing indicator in a Jasper bubble the moment the message leaves.
    var row = el('div', 'jr-help__msg jr-help__msg--assistant');
    row.appendChild(avatar());
    var bubble = el('div', 'jr-help__bubble');
    var typing = typingIndicator();
    bubble.appendChild(typing);
    row.appendChild(bubble);
    list.appendChild(row);
    scrollToBottom(true);

    var streamed = '';
    var finalAnswer = null;
    var failed = false;
    var gotDone = false;

    function onDelta(t) {
      if (firstWordAt === null) firstWordAt = Date.now();
      if (typing && typing.parentNode) { bubble.removeChild(typing); typing = null; playFace(); }
      streamed += t;
      bubble.textContent = streamed;
      scrollToBottom(false);
    }

    function onEvent(ev) {
      if (!ev || typeof ev.type !== 'string') return;
      if (ev.type === 'reply_delta') { if (typeof ev.text === 'string') onDelta(ev.text); }
      else if (ev.type === 'meta') { if (ev.cancel_variant === 'chat' || ev.cancel_variant === 'flow') { cxVariant = ev.cancel_variant; writeJSON(KEY_CX, cxVariant); } }
      else if (ev.type === 'answer') { if (ev.answer) { finalAnswer = ev.answer; if (ev.answer.cancel) lastCancel = ev.answer.cancel; } }
      else if (ev.type === 'error') { failed = true; }
      else if (ev.type === 'done') { gotDone = true; }
      // `meta` carries customer_known / first_name; the greeting is already
      // rendered from Liquid, so nothing to do with it in v1.
    }

    function finish() {
      if (typing && typing.parentNode) bubble.removeChild(typing);
      pendingReply = false;
      if (failed || (!finalAnswer && !streamed)) {
        row.parentNode && row.parentNode.removeChild(row);
        showError(ERROR_COPY);
        track('reply_failed', { ms: Date.now() - sentAt, streamed: streamed.length > 0 });
        setBusy(false);
        return;
      }
      track('reply_received', {
        ms_first_word: firstWordAt ? firstWordAt - sentAt : null, ms_total: Date.now() - sentAt,
        resolution: finalAnswer ? finalAnswer.resolution : null,
        actions: finalAnswer && finalAnswer.actions ? finalAnswer.actions.map(function (a) { return a.kind; }) : [],
        cards: finalAnswer && finalAnswer.cards ? finalAnswer.cards.length : 0,
        escalated: !!(finalAnswer && finalAnswer.escalation && finalAnswer.escalation.needed),
        confidence: finalAnswer ? finalAnswer.confidence : null
      });
      var reply = finalAnswer && typeof finalAnswer.reply === 'string' && finalAnswer.reply ? finalAnswer.reply : streamed;
      setBubbleText(bubble, reply);
      var actions = (finalAnswer && Array.isArray(finalAnswer.actions)) ? finalAnswer.actions : [];
      var cards = (finalAnswer && Array.isArray(finalAnswer.cards)) ? finalAnswer.cards.slice(0, 3) : [];
      var escalated = !!(finalAnswer && finalAnswer.escalation && finalAnswer.escalation.needed)
        || actions.some(function (a) { return a && a.kind === 'escalate'; });
      var msg = { role: 'assistant', text: reply, actions: actions.slice(0, 6), cards: cards, escalated: escalated };
      pushMessage(msg);
      if (cards.length) renderCards(cards);
      if (escalated) list.appendChild(el('div', 'jr-help__status', ESCALATE_COPY));
      if (actions.length) renderActions(actions, true);
      scrollToBottom(false);
      if (finalAnswer && finalAnswer.resolution === 'answered' && !escalated) armCsatAfterAnswer();
      if (!isOpen()) { root.setAttribute('data-unread', 'true'); writeJSON(KEY.unread, true); }
      setBusy(false);
    }

    function rateLimited() {
      pendingReply = false;
      track('rate_limited');
      row.parentNode && row.parentNode.removeChild(row);
      showError(RATE_COPY);
      setTimeout(function () { setBusy(false); }, 5000);
    }

    getCustomerToken().then(function (token) {
      var payload = {
        action: 'chat',
        session_id: sessionId,
        message: text,
        history: history,
        page: pageContext()
      };
      if (token) payload.customer_token = token;

      var controller = window.AbortController ? new AbortController() : null;
      var idle = null;
      function bump() {
        if (idle) clearTimeout(idle);
        idle = setTimeout(function () { failed = true; if (controller) controller.abort(); }, STREAM_IDLE_MS);
      }
      bump();

      return fetch(CFG.endpoint, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      }).then(function (res) {
        if (res.status === 429) { clearTimeout(idle); return 'rate'; }
        if (res.status === 403) { clearTimeout(idle); return 'gated'; }
        if (!res.ok) throw new Error('http ' + res.status);
        if (!res.body || !res.body.getReader) {
          // No streaming support: take the whole body and parse it in one go.
          return res.text().then(function (t) { parseFrames(t + '\n\n', onEvent); });
        }
        var reader = res.body.getReader();
        var decoder = new TextDecoder();
        var buffer = '';
        function pump() {
          return reader.read().then(function (r) {
            if (r.done) {
              // Flush a final frame that arrived without its trailing blank line.
              buffer += decoder.decode();
              if (buffer.trim()) parseFrames(buffer + '\n\n', onEvent);
              return;
            }
            bump();
            buffer += decoder.decode(r.value, { stream: true });
            buffer = parseFrames(buffer, onEvent);
            if (gotDone || failed) { try { reader.cancel(); } catch (e) {} return; }
            return pump();
          });
        }
        return pump();
      }).then(function (r) {
        clearTimeout(idle);
        if (r === 'rate') { rateLimited(); return; }
        if (r === 'gated') {
          // The preview code was wrong or has been rotated: ask again and keep
          // the customer's message in the transcript so a retry carries it.
          row.parentNode && row.parentNode.removeChild(row);
          // "That code didn't work" only when a code was actually tried. A
          // tester who tapped a chip before the gate check finished has never
          // typed one — they get the plain "enter the preview code" step
          // (Ben, 2026-09-17: locked out by a message about a code he never entered).
          var hadCode = !!previewCode;
          previewCode = ''; writeLocal(KEY.preview, '');
          setBusy(false);
          showAccess(hadCode);
          return;
        }
        finish();
      }).catch(function () {
        clearTimeout(idle);
        failed = true;
        finish();
      });
    });
  }

  // Parses every complete SSE frame in `buf` (frames end with a blank line),
  // hands each decoded JSON payload to `cb`, and returns the unparsed tail so
  // a frame split across chunks is completed by the next read.
  function parseFrames(buf, cb) {
    var idx;
    while ((idx = buf.search(/\r?\n\r?\n/)) !== -1) {
      var frame = buf.slice(0, idx);
      buf = buf.slice(idx).replace(/^\r?\n\r?\n/, '');
      var lines = frame.split(/\r?\n/);
      var data = [];
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        if (line.indexOf('data:') === 0) data.push(line.slice(5).replace(/^ /, ''));
        // `event:`, `id:`, comments (`:`) — not used by this contract.
      }
      if (!data.length) continue;
      try { cb(JSON.parse(data.join('\n'))); } catch (e) { /* malformed frame: skip, keep streaming */ }
    }
    return buf;
  }

  // ---- Sign-in continuity ----------------------------------------------------
  // A shopper who started anonymously, went to sign in, and came back: the
  // transcript is still here (localStorage) and the backend row is still keyed
  // on session_id, so tell the backend whose it is now (`claim`, identity from
  // the verified token) and say so in the thread — the next answer will have
  // their account in front of it. Runs once per session_id.
  function claimIfSignedIn() {
    if (!CFG.loggedIn || !transcript.length || readJSON(KEY.claimed) === sessionId) return;
    var hadSignInNudge = transcript.some(function (m) { return m.role === 'assistant' && (m.actions || []).some(function (a) { return a && a.kind === 'sign_in'; }); });
    getCustomerToken().then(function (token) {
      if (!token) return;
      return fetch(CFG.endpoint, { method: 'POST', headers: headers(), body: JSON.stringify({ action: 'claim', session_id: sessionId, customer_token: token }) })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          if (!j || !j.ok || !j.customer_known) return;
          writeJSON(KEY.claimed, sessionId);
          track('signed_in_return', { had_nudge: hadSignInNudge });
          var who = j.first_name ? j.first_name + ', you' : 'You';
          var note = { role: 'status', text: who + '’re signed in — I can see your orders and subscriptions now.' + (hadSignInNudge ? ' Pick up where we left off.' : '') };
          pushMessage(note);
          if (isOpen()) { renderMessage(note); scrollToBottom(true); }
        });
    }).catch(function () {});
  }

  // ---- Past conversations (signed in) ---------------------------------------
  // Jon, 2026-09-17: "yes to the logged in state having history." A History
  // control in the header swaps a read-only list of the customer's earlier
  // conversations in over the thread; tapping one shows its turns. Everything
  // comes from the backend's `history` / `transcript` actions, which answer
  // only for the verified customer token — nothing here is cached beyond the view.
  var historyBtn = $('JrHelpHistoryBtn');
  var historyView = $('JrHelpHistory');
  function fmtWhen(iso) {
    var d = new Date(iso); if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  function historyOpen() { return !!historyView && !historyView.hidden; }
  function showHistory(on) {
    if (!historyView) return;
    historyView.hidden = !on;
    body.hidden = on;
    form.hidden = on || (access && !access.hidden);
    if (historyBtn) historyBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (!on) { renderTranscript(); scrollToBottom(true); }
  }
  function historyList() {
    historyView.innerHTML = '';
    historyView.appendChild(el('div', 'jr-help__history-title', 'Your past conversations'));
    var note = el('div', 'jr-help__status', 'Loading…');
    historyView.appendChild(note);
    getCustomerToken().then(function (token) {
      if (!token) throw new Error('no token');
      return fetch(CFG.endpoint, { method: 'POST', headers: headers(), body: JSON.stringify({ action: 'history', customer_token: token }) }).then(function (r) { return r.json(); });
    }).then(function (j) {
      if (!j || !j.ok) throw new Error('failed');
      note.remove();
      var rows = (j.conversations || []).filter(function (c) { return c.session_id !== sessionId; });
      if (!rows.length) { historyView.appendChild(el('div', 'jr-help__status', 'This is your first conversation with me.')); return; }
      rows.forEach(function (c) {
        var b = btn('jr-help__history-item', '', function () { track('history_viewed', { turns: c.turns }); historyTranscript(c); });
        b.appendChild(el('span', 'jr-help__history-when', fmtWhen(c.started_at)));
        b.appendChild(el('span', 'jr-help__history-opening', c.opening || '(no message)'));
        var meta = [c.turns + (c.turns === 1 ? ' message' : ' messages'), c.csat === 'solved' ? 'solved' : c.csat === 'not_solved' ? 'not solved' : '', c.escalated ? 'a specialist followed up' : ''].filter(Boolean).join(' · ');
        b.appendChild(el('span', 'jr-help__history-meta', meta));
        historyView.appendChild(b);
      });
    }).catch(function () { note.textContent = 'I couldn’t load your conversations just now.'; });
  }
  function historyTranscript(c) {
    historyView.innerHTML = '';
    var back = btn('jr-help__chip', '‹ All conversations', function () { historyList(); });
    historyView.appendChild(back);
    historyView.appendChild(el('div', 'jr-help__history-title', fmtWhen(c.started_at)));
    var note = el('div', 'jr-help__status', 'Loading…');
    historyView.appendChild(note);
    getCustomerToken().then(function (token) {
      return fetch(CFG.endpoint, { method: 'POST', headers: headers(), body: JSON.stringify({ action: 'transcript', customer_token: token, session_id: c.session_id }) }).then(function (r) { return r.json(); });
    }).then(function (j) {
      if (!j || !j.ok) throw new Error('failed');
      note.remove();
      var wrap = el('div', 'jr-help__messages');
      (j.turns || []).forEach(function (t) {
        var me = el('div', 'jr-help__msg jr-help__msg--customer'); var mb = el('div', 'jr-help__bubble'); setBubbleText(mb, t.customer); me.appendChild(mb); wrap.appendChild(me);
        var him = el('div', 'jr-help__msg jr-help__msg--assistant'); him.appendChild(avatar()); var hb = el('div', 'jr-help__bubble'); setBubbleText(hb, t.reply); him.appendChild(hb); wrap.appendChild(him);
      });
      historyView.appendChild(wrap);
    }).catch(function () { note.textContent = 'I couldn’t load that conversation just now.'; });
  }
  if (historyBtn && historyView) historyBtn.addEventListener('click', function () {
    if (historyOpen()) { showHistory(false); return; }
    track('history_opened');
    showHistory(true);
    historyList();
  });

  // ---- The help page talks to the widget -----------------------------------------
  // /pages/help hosts the chat IN THE PAGE (Jon, 2026-09-17: typing at the top
  // and having a popup appear at the side "feels broken"; and the navy panel
  // header vanished into the page's navy hero). `jr-help:inline` moves this
  // same widget — same session, same transcript — into a host element in the
  // page flow: no launcher, no close, no body lock, the panel is just a block.
  // Its own small events ride this widget's analytics channel so they land in
  // the same session.
  document.addEventListener('jr-help:inline', function (e) {
    var host = e && e.detail && e.detail.host;
    if (!host || inline) return;
    inline = true;
    host.appendChild(root);
    root.classList.add('jr-help--inline');
    open(false);
    document.documentElement.classList.remove('jr-help-locked');
    scrollToBottom(true);
    track('opened', { inline: true, turns: transcript.filter(function (m) { return m.role === 'customer'; }).length, signed_in: !!CFG.loggedIn });
  });
  document.addEventListener('jr-help:ask', function (e) {
    var text = e && e.detail && String(e.detail.text || '').trim();
    if (!text) return;
    track('chip_clicked', { chip: 'help-center:' + text.slice(0, 40) });
    open(true);
    if (!guestChosen()) writeJSON(KEY.guest, true);
    var go = function () { if (!busy) send(text); else setTimeout(go, 400); };
    setTimeout(go, reduceMotion() ? 0 : 200);
  });
  // Plain open / minimise, for the cart drawer (snippets/jr-cart-open): the
  // drawer minimises the chat when it opens and its "Ask Jasper" link opens
  // it. close() only hides the panel; the conversation stays.
  document.addEventListener('jr-help:open', function () { open(true); });
  document.addEventListener('jr-help:close', function () { close(); });
  document.addEventListener('jr-help:track', function (e) {
    var d = e && e.detail; if (!d || !d.name) return;
    track(String(d.name).replace(/^help_/, ''), d.props || {});
  });

  // ---- Boot --------------------------------------------------------------
  // The help page's script is deferred too and sits earlier in the document, so
  // it can run before these listeners exist: it waits for this flag/event.
  root.setAttribute('data-ready', 'true');
  document.dispatchEvent(new CustomEvent('jr-help:ready'));
  renderTranscript();
  claimIfSignedIn();
  if (readJSON(KEY.unread)) root.setAttribute('data-unread', 'true');
  // Carry an open panel across page navigations on desktop so a conversation
  // survives a click into the catalogue. On a phone the sheet is full-screen,
  // so re-opening it over the page they just navigated to would hide it — the
  // exception is the return from sign-in (Shopify marks it ?shop_sign_in=true),
  // where the chat IS what they came back for.
  var signedInReturn = /[?&]shop_sign_in=true/.test(location.search);
  if (readJSON(KEY.open) === true && (!isPhone() || signedInReturn)) open(false);
})();
